import React, { useEffect, useState, useCallback } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import PropTypes from 'prop-types';
import {
  ListVariant,
  TradeState,
  TradeViewVariant
} from '../../../_shared/types/trade';
import ListCard from '../../../_shared/components/ListCard';
import ActiveTimer from '../../../_shared/components/ActiveTimer';
import Title from './Title';
import Content from './Content';
import Footer from './Footer';
import useLiveBids from '../../../_shared/hooks/useLiveBids';

import { StyledInvalidTradeCard } from './Styles';
import useTradeChange from '../../../_shared/hooks/useTradeChange';
import ReducerConstants from '../../../_shared/constants/reducers';

const propTypes = {
  tradeSummary: PropTypes.shape({
    id: PropTypes.number,
    thumbnailUrl: PropTypes.string,
    year: PropTypes.number,
    make: PropTypes.string,
    model: PropTypes.string
  }).isRequired,
  onSelect: PropTypes.func,
  variant: PropTypes.oneOf([
    ListVariant.LIST,
    ListVariant.GRID,
    ListVariant.SEARCH
  ]).isRequired,
  isInSaleEvent: PropTypes.bool,
  isLastTradeInSaleEvent: PropTypes.bool,
  redirectLink: PropTypes.string,
  bidModalRef: PropTypes.objectOf(PropTypes.any)
};

const defaultProps = {
  onSelect: () => {},
  isInSaleEvent: false,
  isLastTradeInSaleEvent: false,
  redirectLink: undefined,
  bidModalRef: undefined
};

const ListTradeCard = ({
  id,
  testId,
  tradeSummary,
  onSelect,
  isInSaleEvent,
  isLastTradeInSaleEvent,
  variant,
  redirectLink,
  onConfirmOpen,
  pendingActiveProxy,
  bidModalRef,
  isOmp = false
}) => {
  const [shouldShowTrade, setShouldShowTrade] = useState(true);
  const { activeExpiryTime } = tradeSummary;
  const [isStateChangedToActive, setIsStateChangedToActive] = useState(false);
  const [isAuctionEnded, setIsAuctionEnded] = useState(false);
  const [tradeCardSummary, setTradeCardSummary] = useState(tradeSummary);
  const [pendingActiveBid, setPendingActiveBid] = useState(null);
  const [isTimerExtended, setIsTimerExtended] = useState(false);

  const stateChange = useSelector(
    state => state?.[ReducerConstants.STATE_CHANGE][tradeCardSummary?.id],
    shallowEqual
  );

  const onCardClick = e => {
    if (variant !== ListVariant.SEARCH && isAuctionEnded) {
      e.preventDefault();
      return false;
    }

    if (e?.ctrlKey || e?.metaKey) {
      return false;
    }

    onSelect(tradeCardSummary?.id, tradeCardSummary?.tradeStateName);
    return true;
  };

  const onPendingActiveBid = ({ tradeId, bidAmount }) => {
    if (tradeCardSummary?.id === tradeId) {
      setPendingActiveBid({ tradeId, bidAmount });
    }
  };

  useTradeChange(tradeCardSummary?.id, setTradeCardSummary);

  // Update Trade Summary object if it was recently fetched
  useEffect(() => {
    if (tradeSummary) {
      setTradeCardSummary(tradeSummary);
    }
  }, [tradeSummary]);

  // detect if trade status changed to active and update state accordingly
  useEffect(() => {
    const auctionStarted =
      stateChange?.tradeId === tradeCardSummary.id &&
      stateChange?.previousState !== TradeState.active &&
      stateChange?.newState === TradeState.active;

    if (auctionStarted) {
      setIsStateChangedToActive(true);
    }
  }, [stateChange, tradeCardSummary]);

  useLiveBids(
    tradeCardSummary,
    setTradeCardSummary,
    pendingActiveProxy,
    pendingActiveBid,
    bidModalRef,
    isTimerExtended,
    setIsTimerExtended
  );

  // Prevent unneccessry rerender of ActiveTimer component
  const onAuctionEnded = useCallback(() => {
    setIsAuctionEnded(true);
  }, []);

  const content = (
    <Content
      testId={`${testId}-content`}
      tradeSummary={tradeCardSummary}
      setTradeSummary={setTradeCardSummary}
      isAuctionEnded={isAuctionEnded}
      onConfirmOpen={onConfirmOpen}
      variant={variant}
      onPendingActiveBid={onPendingActiveBid}
      bidModalRef={bidModalRef}
      onAuctionEnded={onAuctionEnded}
      isOmp={isOmp}
    />
  );

  const title = (
    <Title
      tradeSummary={tradeCardSummary}
      variant={variant}
      isStateChangedToActive={isStateChangedToActive}
    />
  );

  const footer = (
    <Footer
      tradeSummary={tradeCardSummary}
      variant={variant}
      tradeState={tradeCardSummary?.tradeState}
      setShouldShowTrade={setShouldShowTrade}
      bidModalRef={bidModalRef}
    />
  );

  const activeTimer =
    tradeCardSummary?.tradeState === TradeState.active &&
    variant === ListVariant.LIST ? (
      <ActiveTimer
        onAuctionEnded={onAuctionEnded}
        activeExpiryTime={
          tradeCardSummary?.activeExpiryTime || activeExpiryTime
        }
        location={TradeViewVariant.LIST}
        tradeId={tradeCardSummary?.id}
        tradeCardAuctionEnded={isAuctionEnded}
        isTimerExtended={
          isTimerExtended || tradeCardSummary?.auctionSummary?.isTimerExtended
        }
      />
    ) : (
      undefined
    );

  return (
    <>
      {shouldShowTrade && (
        <ListCard
          title={title}
          content={content}
          footer={footer}
          activeTimer={activeTimer}
          id={id}
          testId={testId}
          variant={variant}
          isInSaleEvent={isInSaleEvent}
          isLastTradeInSaleEvent={isLastTradeInSaleEvent}
          thumbnailUrl={tradeCardSummary?.thumbnailUrl}
          onClick={e => onCardClick(e)}
          redirectLink={redirectLink}
          bidStatus={
            tradeCardSummary?.tradeState === TradeState.active
              ? tradeCardSummary?.auctionSummary?.userBidStatus
              : 1
          }
          isTimerExtended={
            isTimerExtended || tradeCardSummary?.auctionSummary?.isTimerExtended
          }
          isOmp={isOmp}
        />
      )}
      {(isAuctionEnded ||
        (tradeCardSummary.tradeState === TradeState.upcoming &&
          isStateChangedToActive)) && (
        <StyledInvalidTradeCard
          data-testid={`${testId}-auction-ended`}
          isInSaleEvent={isInSaleEvent}
          variant={
            (tradeCardSummary?.tradeState === TradeState.upcoming ||
              tradeCardSummary?.tradeState === TradeState.active) &&
            tradeCardSummary?.auctionSummary?.userBidStatus
          }
        />
      )}
    </>
  );
};

ListTradeCard.propTypes = propTypes;
ListTradeCard.defaultProps = defaultProps;

export default ListTradeCard;
